import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Chip, Typography } from '@mui/material';
import { DesktopWindowsOutlined, ArrowForwardOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { alpha, useTheme } from '@mui/material/styles';
import { fetchLatestRelease } from '../services/windowsClientService';
import { Card, CardContent, Button } from '../design-system/components';
import colors from '../design-system/colors';

/**
 * Dashboard card/banner showing the latest Windows client release.
 * Release metadata is optional; the single detail-page CTA is always available.
 */
const WindowsClientBanner = () => {
  const { t } = useTranslation('windowsClient');
  const theme = useTheme();

  const { data: release, isLoading, isError } = useQuery({
    queryKey: ['windows-release'],
    queryFn: fetchLatestRelease,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        variant="outlined"
        aria-label={
          release
            ? t('banner.ariaLabel', { version: release.version })
            : t('banner.title')
        }
        sx={{ borderLeft: `4px solid ${colors.primary[500]}` }}
      >
        <CardContent>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <DesktopWindowsOutlined
              sx={{ color: colors.primary[600], fontSize: 28 }}
              aria-hidden="true"
            />
            <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ flex: 1 }}>
              {t('banner.title')}
            </Typography>
            {release?.version && (
              <Chip
                label={t('banner.versionLabel', { version: release.version })}
                size="small"
                sx={{
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.2)
                    : colors.primary[50],
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : colors.primary[700],
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: 24,
                }}
              />
            )}
          </Box>

          {/* Loading state */}
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <CircularProgress size={16} aria-label={t('banner.loadingText')} />
              <Typography variant="body2" color="text.secondary">
                {t('banner.loadingText')}
              </Typography>
            </Box>
          )}

          {/* Release failure must not prevent navigation. */}
          {isError && !isLoading && (
            <Alert
              severity="warning"
              sx={{ py: 0.5 }}
            >
              {t('banner.errorText')}
            </Alert>
          )}

          {/* Success state */}
          {release && !isLoading && !isError && (
            <>
              {release.changelog && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {release.changelog}
                </Typography>
              )}

            </>
          )}
          <Button
            variant="contained"
            component={Link}
            to="/cliente-windows"
            endIcon={<ArrowForwardOutlined />}
            sx={{ minHeight: 44, minWidth: 44, mt: 1.5 }}
          >
            {t('banner.viewMore')}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default WindowsClientBanner;
